import { ALL_CONTACT_KEYS } from './contactKeys';
import { ABOUT_ME_FIELDS } from './profilePayload';
import { isLiveSharingReadOn } from './newExperience';
import { isPeerGone } from './peerGone';

// ── Live sharing (D2) — the fields the get_shared_items resolver OWNS ────────
// The fixed contact slots plus the two pseudo-items. Under live sharing these
// are set WHOLESALE from the resolver on every People/chat load — present = the
// holder's current value, absent = removed — so a detail the holder turned off
// or deleted disappears instead of additively surviving on the cached copy.
const LIVE_SHARED_FIELDS = [...ALL_CONTACT_KEYS, 'currentCity', 'aboutMe'];
// The aboutMe bundle (role/organization/whatIDo/placesLived — profilePayload's
// single source of truth) is not a sharing item in the new model. Clear these
// under live sharing so they can never ride the shared 'aboutMe' flag (the leak
// guard, Mark 2026-10-14) — they appear only on old pre-flag test records.
const LIVE_SHARED_LEAK_GUARD = ABOUT_ME_FIELDS;
// Resolver-owned fields cleared on a wholesale replace / history strip, as one
// hoisted array (no per-connection concat) + a Set for O(1) flag membership.
// currentCityPlace (where the hometown is) goes wherever currentCity goes: the
// resolver has no item for it yet, so under live sharing it is cleared — a
// hometown turned off never leaves its point behind.
const LIVE_SHARED_CLEARED = [...LIVE_SHARED_FIELDS, 'currentCityPlace', ...LIVE_SHARED_LEAK_GUARD];
const LIVE_SHARED_FLAG_SET = new Set(LIVE_SHARED_FIELDS);
// resolver item_key → the peer-blob field it maps to (contact slots + 'aboutMe'
// map to themselves; only livesIn differs).
const ITEM_KEY_TO_FIELD = { livesIn: 'currentCity' };

/**
 * Connection name precedence + editability — one predicate, two consumers.
 *
 * displayName is the owner's local rename for a connection. It belongs to
 * connections whose name the owner owns: anon contacts, manual contacts, and
 * deleted-profile peers. An ACTIVE Royale peer owns their own name — they
 * share it live (full vs first) — so the override never applies there:
 * render the live shared name, and don't allow edits.
 */

/**
 * True when the peer is an active Royale user: has a profile and hasn't gone —
 * neither deleted their account nor left this connection (CD #15). A gone peer's
 * name becomes editable (the displayName override applies) and their live shared
 * name no longer renders.
 * @param {Object|null} anonData - The connection's peer data (userData / anonData)
 * @returns {boolean}
 */
export const isActiveRoyalePeer = (anonData) =>
  !!anonData?.senderProfileUserId && !isPeerGone(anonData);

/**
 * True when the peer consented to share their hometown — the flag half of the
 * hometown gate. 'currentCity' = standalone share; 'aboutMe' = the legacy
 * bundle that carried hometown before the split (user ruling 2026-07-14: the
 * old bundle share was and continues to be a hometown share).
 *
 * Consent lives ONLY in sharedFields — a present currentCity value is NOT
 * proof of consent (refresh paths used to stamp values without it). No value
 * check here: refresh paths use this as their carry gate, and a
 * flagged-but-valueless record still receives value refreshes.
 *
 * @param {Object|null} userData - The peer's shared data (anonData or profileData)
 * @returns {boolean}
 */
export const hasHometownShareFlag = (userData) => {
  const fields = userData?.sharedFields;
  return !!fields && (fields.includes('currentCity') || fields.includes('aboutMe'));
};

/**
 * True when a peer's hometown may display: consented AND value present. The
 * single gate for every hometown surface — detail section, People card,
 * People-list search, Nearby fallback, Anon list. No hand-copied gates.
 *
 * Shape-agnostic: Profile surfaces pass the peer's anonData, Anon surfaces
 * pass the peer's profileData — both carry sharedFields + currentCity.
 *
 * @param {Object|null} userData - The peer's shared data (anonData or profileData)
 * @returns {boolean}
 */
export const isHometownShared = (userData) =>
  hasHometownShareFlag(userData) && !!userData?.currentCity;

/**
 * The consent-gated hometown read: the value when shared, null otherwise.
 * Display surfaces use this instead of reading currentCity directly, so an
 * ungated read can't slip in silently.
 *
 * @param {Object|null} userData - The peer's shared data (anonData or profileData)
 * @returns {string|null}
 */
export const getSharedHometown = (userData) =>
  isHometownShared(userData) ? userData.currentCity : null;

/**
 * Two-letter initials for card avatars ("Mark Mangan" → "MM"). Single names
 * give one letter; empty input gives "?". One home for the three card files
 * that used to hand-copy this (ConnectionCard, AnonPreviewCard, ProximityCard).
 */
export const getInitials = (fullName) => {
  if (!fullName) return '?';
  const words = fullName.trim().split(/\s+/);
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
};

/**
 * The name to display for a connection.
 *
 * Active Royale peer → their live shared name, never the override (a live
 * peer's missing name is a data problem; masking it with a stale override
 * would hide that). Everyone else (anon, manual, deleted peer) → the owner's
 * rename first, then the shared name.
 *
 * @param {Object|null} connection - Needs { displayName, anonData }
 * @returns {string|null}
 */
export const getConnectionDisplayName = (connection) => {
  if (!connection) return null;
  const { displayName, anonData } = connection;
  if (isActiveRoyalePeer(anonData)) return anonData.name || null;
  return displayName || anonData?.name || null;
};

/**
 * Apply a profiles-table row to a connection's peer data (list enrichment).
 *
 * Fill-missing ONLY for the name: the profiles table holds the peer's RAW
 * full name and doesn't know what form they chose to share (nameSharing) —
 * the shared name in anonData is authoritative; the profile name only fills
 * a blank. Photos aren't nameSharing-gated and refresh freely.
 *
 * senderProfileUserId is fill-missing too: the RPC only returns rooms whose
 * peer IS a Royale profile, so stamping it here marks converted peers as
 * profile peers on list load — Nearby membership and the deletion latch no
 * longer wait for the viewer to open that chat (convert-rekey 5b). A room
 * the RPC stops returning (peer deleted) simply never reaches this function,
 * so an existing marker + peerProfileDeleted latch are never disturbed.
 *
 * Hometown (currentCity) carries here for the same reason the marker does:
 * the chat-open peer-upgrade detection also carries it, but its gate ("photo
 * OR marker missing") goes false once this function stamps both — so this
 * path must carry hometown or list-stamped peers never get it. The carry is
 * value-refresh ONLY, gated on the record already passing the share-flag
 * gate (hasHometownShareFlag): the flag is NEVER added here — consent comes
 * from an explicit share (or the Conversion Grant), never from enrichment.
 *
 * SIMPLE-profile peers are a second regime (user ruling 2026-08-08, device
 * pass 3): at Simple tier the profile page IS the sharing record ("This is
 * what your connections see"), so an SP's later additions push to every
 * connection automatically — values AND sharedFields flags. The consent
 * boundary is enforced SERVER-side: profile.card_data is non-NULL only for
 * genuine SPs (phone-less + not-full, migration 029), so this branch can
 * never touch a Full peer's record — the no-flag-grant rule above stays
 * intact for the Full regime. Additive only: an SP deleting a card field
 * does not remove it here (accepted boundary, logged in the plan).
 *
 * @param {Object|null} anonData - The connection's current peer data
 * @param {Object|null} profile - profiles-table row ({ name, photo_url, profile_user_id, current_city, account_status, card_data, photo_meta, colour })
 * @returns {Object|null} Updated anonData, or null when nothing changed
 */
// Key-order-blind equality for small JSON values (a jsonb read comes back
// with its keys reordered, so a plain stringify would see a change every load).
const canonical = (v) =>
  v && typeof v === 'object'
    ? Array.isArray(v)
      ? `[${v.map(canonical).join(',')}]`
      : `{${Object.keys(v)
          .sort()
          .map((k) => `${JSON.stringify(k)}:${canonical(v[k])}`)
          .join(',')}}`
    : JSON.stringify(v);
const sameJson = (a, b) => canonical(a) === canonical(b);

export const applyProfileToAnonData = (anonData, profile, { liveSharing = false } = {}) => {
  if (!profile?.name) return null;
  const needsName = !anonData?.name;
  const needsPhoto = !!profile.photo_url && profile.photo_url !== anonData?.photoUrl;
  const needsPeerId = !!profile.profile_user_id && !anonData?.senderProfileUserId;
  // Under live sharing the resolver owns hometown (the 'livesIn' item) and every
  // contact detail; suppress the pre-D2 carries here (this hometown refresh and
  // the SP card push below) so they can't additively survive a turn-off/delete —
  // applySharedItemsToAnonData then sets the resolver's wholesale set.
  // CONTRACT: any auto-push added here for a field in LIVE_SHARED_FIELDS must
  // gate on !liveSharing too, or it reintroduces that leak on the offline branch
  // (enrichPeerAnonData runs only this function when the resolver is unreadable).
  // The hometown's picked point rides with it (current_city_place, 051 — absent
  // before 051, then nothing changes).
  const needsHometown =
    !liveSharing &&
    !!profile.current_city &&
    hasHometownShareFlag(anonData) &&
    (anonData?.currentCity !== profile.current_city ||
      (profile.current_city_place !== undefined &&
        !sameJson(profile.current_city_place || null, anonData?.currentCityPlace || null)));
  // Tier marker for the Royale ring (Full-only, user ruling 2026-08-08) —
  // refresh-on-change, not fill-once: a peer's SP→Full conversion upgrades
  // the ring on the next list load
  const needsStatus =
    !!profile.account_status && anonData?.peerAccountStatus !== profile.account_status;
  // The photo's meta (face box + the person's colour) refreshes with the photo
  // — it describes the photo, never a sharing choice (signup-photo-colour-
  // engine step 7). The column exists only where migration 033 ran; elsewhere
  // photo_meta is undefined and nothing changes.
  const needsMeta = !!profile.photo_meta && !sameJson(profile.photo_meta, anonData?.photoMeta);
  // The person's chosen colour (signup A′) — shown to every holder, refreshed on
  // change. The column exists only where migration 034 ran; elsewhere (and for
  // anyone who hasn't chosen) colour is undefined/null and nothing changes.
  const needsColour = !!profile.colour && profile.colour !== anonData?.colour;

  // SP card push — see the docblock's second regime. card.name is the
  // validity guard: a malformed/empty card row changes nothing. Suppressed under
  // live sharing (the resolver owns contacts + city — see needsHometown above).
  const card = liveSharing ? null : profile.card_data;
  const cardAdds = {};
  let cardFlags = null;
  if (card?.name) {
    const cardFields = ['photoUrl', 'currentCity', ...ALL_CONTACT_KEYS].filter((k) => card[k]);
    for (const k of cardFields) {
      if (anonData?.[k] !== card[k]) cardAdds[k] = card[k];
    }
    // The card's hometown brings its point (not a flag — it's part of the hometown).
    if (cardAdds.currentCity && card.currentCityPlace) {
      cardAdds.currentCityPlace = card.currentCityPlace;
    }
    if (card.name !== anonData?.name) cardAdds.name = card.name;
    const existingFlags = anonData?.sharedFields || [];
    const wantedFlags = ['name', ...cardFields];
    if (wantedFlags.some((k) => !existingFlags.includes(k))) {
      cardFlags = Array.from(new Set([...existingFlags, ...wantedFlags]));
    }
  }
  const hasCardChanges = Object.keys(cardAdds).length > 0 || !!cardFlags;

  if (
    !needsName &&
    !needsPhoto &&
    !needsPeerId &&
    !needsHometown &&
    !needsStatus &&
    !needsMeta &&
    !needsColour &&
    !hasCardChanges
  )
    return null;
  const updated = { ...anonData };
  if (needsName) updated.name = profile.name;
  if (needsPhoto) updated.photoUrl = profile.photo_url;
  if (needsPeerId) updated.senderProfileUserId = profile.profile_user_id;
  if (needsHometown) {
    updated.currentCity = profile.current_city;
    if (profile.current_city_place) updated.currentCityPlace = profile.current_city_place;
    else if (profile.current_city_place === null) delete updated.currentCityPlace;
  }
  if (needsStatus) updated.peerAccountStatus = profile.account_status;
  if (needsMeta) updated.photoMeta = profile.photo_meta;
  if (needsColour) updated.colour = profile.colour;
  Object.assign(updated, cardAdds);
  if (cardFlags) updated.sharedFields = cardFlags;
  return updated;
};

/**
 * applySharedItemsToAnonData — wholesale-replace a peer's live-shared items on
 * the cached blob from the D2 resolver (get_shared_items). The crux of live
 * sharing: REPLACE, never merge. For the resolver-owned fields (contact slots +
 * currentCity + aboutMe) and their share flags, the result mirrors EXACTLY what
 * the resolver returned — a value the holder changed updates, a detail they
 * turned off or deleted is removed (value AND flag), and nothing left over
 * additively survives. Auto-shared fields (name, photo, colour, status, …) are
 * left untouched — those are applyProfileToAnonData's.
 *
 * `items` is the resolver's rows for ONE room: [{ itemKey, value }]. An empty
 * array means the holder currently shares nothing → every live-shared field is
 * cleared. (The caller passes [] only on a SUCCESSFUL empty read; an unreadable
 * resolver — offline / error — is handled by enrichPeerAnonData, which skips
 * this so the offline cache stands instead of being wiped.)
 *
 * Pure. Returns a new blob, or null when nothing changed.
 *
 * @param {Object|null} anonData
 * @param {Array<{itemKey:string,value:*}>} [items]
 * @returns {Object|null}
 */
export const applySharedItemsToAnonData = (anonData, items = []) => {
  const base = anonData || {};
  const visible = {};
  for (const it of items) {
    if (!it?.itemKey) continue;
    visible[ITEM_KEY_TO_FIELD[it.itemKey] || it.itemKey] = it.value;
  }
  const updated = { ...base };
  // `changed` is tracked incrementally over only the fields this touches — the
  // resolver-owned subset + sharedFields; every other field is copied unchanged.
  // (A full-blob deep compare here ran per connection on the hot People path.)
  let changed = false;
  // Set each visible item to its live value.
  for (const f in visible) {
    if (updated[f] !== visible[f]) {
      updated[f] = visible[f];
      changed = true;
    }
  }
  // Remove resolver-owned fields (and the leak-guard bundle fields) not now visible.
  for (const f of LIVE_SHARED_CLEARED) {
    if (!(f in visible) && f in updated) {
      delete updated[f];
      changed = true;
    }
  }
  // Share flags mirror the visible set: drop every resolver-owned flag, add back
  // exactly the visible ones. Non-shared flags (name, photoUrl, colour) are kept.
  const prevFlags = base.sharedFields || [];
  const nextFlags = Array.from(
    new Set([...prevFlags.filter((k) => !LIVE_SHARED_FLAG_SET.has(k)), ...Object.keys(visible)])
  );
  if (nextFlags.length !== prevFlags.length || !nextFlags.every((k) => prevFlags.includes(k))) {
    changed = true;
  }
  updated.sharedFields = nextFlags;
  return changed ? updated : null;
};

/**
 * Strip the resolver-owned items (contact slots + currentCity + aboutMe) and
 * their share flags from a history-derived blob, so the recovery composition
 * does NOT rebuild live-shared details from old chat messages under live
 * sharing — the resolver (online) / the offline cache own them. Non-shared
 * fields (name, photoUrl, senderProfileUserId, …) pass through. Pure.
 *
 * @param {Object|null} blob
 * @returns {Object|null}
 */
export const withoutLiveSharedFields = (blob) => {
  if (!blob) return blob;
  const out = { ...blob };
  for (const f of LIVE_SHARED_CLEARED) delete out[f];
  if (Array.isArray(out.sharedFields)) {
    out.sharedFields = out.sharedFields.filter((k) => !LIVE_SHARED_FLAG_SET.has(k));
  }
  return out;
};

// Fields the person's live profile owns: the People list refreshes them from
// it (applyProfileToAnonData), so old chat history must never put them back.
const LIVE_PROFILE_OWNED = ['colour', 'photoUrl', 'photoMeta'];

/**
 * Keep the stored record's live-profile fields over a history-derived
 * recovery: chat history fills them only when the record has none. Without
 * this, opening Detail re-applied a colour or photo the person has since
 * changed, and People and Detail overwrote each other (f/people-contact-rule,
 * 2026-10-15). Pure.
 *
 * @param {Object|null} recovered - the composed recovery
 * @param {Object|null} stored - the record before recovery
 * @returns {Object|null}
 */
export const keepLiveProfileFields = (recovered, stored) => {
  if (!recovered || !stored) return recovered;
  const out = { ...recovered };
  for (const f of LIVE_PROFILE_OWNED) {
    if (stored[f]) out[f] = stored[f];
  }
  return out;
};

/**
 * The People/chat read-path composition for ONE connection, and the single home
 * of the live-sharing contract (so the three call sites don't re-author it): run
 * the auto-shared enrichment (name/photo/colour/status/…), then, under live
 * sharing, wholesale-replace the resolver-owned items.
 *
 * `sharedByRoom` is getSharedItems' whole-roster map, or NULL when the resolver
 * was unreadable (flag off / offline / RPC error) — in which case the cached
 * (last-resolver) items stand untouched (never wiped). A room absent from a
 * non-null map = the holder shares nothing → that room's items are cleared.
 * Returns the new blob, or null when nothing changed (the caller skips persist).
 *
 * @param {Object|null} anonData
 * @param {Object|null} profile - the get_connection_profiles row for this room
 * @param {Object<string,Array>|null} sharedByRoom - getSharedItems result, or null
 * @param {string} chatRoomId
 * @returns {Object|null}
 */
export const enrichPeerAnonData = (anonData, profile, sharedByRoom, chatRoomId) => {
  // Gated on the D2 READ switch (off until the backfill), NOT NEW_EXPERIENCE:
  // off → applyProfileToAnonData runs pre-D2 (card/hometown carry) and the
  // resolver wholesale-replace never runs, so the empty-table wipe can't happen.
  const liveSharing = isLiveSharingReadOn();
  const afterProfile = applyProfileToAnonData(anonData, profile, { liveSharing });
  // sharedByRoom is non-null only when the flag is on AND the read succeeded
  // (getSharedItems returns null otherwise), so this also covers the flag-off
  // and offline cases — the cache stands.
  if (sharedByRoom == null) return afterProfile;
  const base = afterProfile || anonData;
  return applySharedItemsToAnonData(base, sharedByRoom[chatRoomId] || []) || afterProfile;
};
